import styles from "./article-art.module.css";

type Motif =
  | "silicon"
  | "branches"
  | "feedback"
  | "grammar"
  | "memory"
  | "receipt"
  | "gates"
  | "interfaces"
  | "context"
  | "boundary"
  | "probe";

const motifs: Record<string, Motif> = {
  "what-should-a-twin-learn-from-a-mistake": "context",
  "qwen-custom-kernel-blackwell": "silicon",
  "china-is-winning-the-post-transformer-race": "branches",
  "the-token-is-a-narrow-feedback-channel": "feedback",
  "rfq1-relation-first-query-language": "grammar",
  "teaching-qwen-to-remember": "memory",
  "every-ai-answer-needs-a-receipt": "receipt",
  "five-gates-to-a-governed-answer": "gates",
  "one-twin-many-interfaces": "interfaces",
  "connector-is-not-context": "context",
  "warehouse-sql-safety": "grammar",
  "context-is-a-security-boundary": "boundary",
  "agent-is-the-easy-part": "interfaces",
  cubesandbox: "boundary",
  "a-skill-is-a-hypothesis": "gates",
  "agentic-workflows-on-xero": "context",
  "agentic-workflows-on-sunsystems": "context",
  "customer-controlled-ai": "boundary",
  "approved-meaning-not-more-context": "context",
  "skills-not-prompts": "grammar",
  "llm-agnostic-by-design": "branches",
  "who-remembers-the-company": "memory",
  "you-are-not-the-front-door": "interfaces",
  "talk-is-not-authority": "boundary",
  "operational-reporting-disconnect": "context",
  "on-prem-ai-for-finance": "boundary",
  "kimi-k2-6": "branches",
  "fingerprinting-jev-for-three-cents": "probe",
  "running-a-portfolio-with-a-digital-twin": "interfaces",
};

/** Conceptual cover drawings, not measurements or product screenshots. */
export function ArticleArt({ slug }: { slug: string }) {
  const motif = motifs[slug];
  if (!motif) return null;
  return (
    <div className={styles.art} data-motif={motif} aria-hidden="true">
      <svg viewBox="0 0 320 190" fill="none" focusable="false">
        <g stroke="currentColor" strokeWidth="1">
          <path
            d="M16 24h10M21 19v10M294 24h10M299 19v10M16 166h10M21 161v10M294 166h10M299 161v10"
            opacity=".35"
          />
          {motif === "silicon" ? (
            <>
              <path
                d="M0 65h80M0 95h80M0 125h80M240 65h80M240 95h80M240 125h80M130 0v32M160 0v32M190 0v32M130 158v32M160 158v32M190 158v32"
                opacity=".35"
              />
              <rect x="80" y="32" width="160" height="126" rx="4" />
              {Array.from({ length: 48 }, (_, i) => (
                <rect
                  key={i}
                  x={95 + (i % 8) * 17}
                  y={47 + Math.floor(i / 8) * 17}
                  width="11"
                  height="11"
                  fill={i % 9 < 4 ? "currentColor" : "none"}
                  opacity={i % 9 < 4 ? ".7" : ".3"}
                />
              ))}
            </>
          ) : null}
          {motif === "branches" ? (
            <>
              <path
                d="M28 95h55M83 95C117 95 110 44 145 44h75M83 95h137M83 95c34 0 27 51 62 51h75"
                strokeWidth="2"
              />
              {[44, 95, 146].map((y, i) => (
                <g key={y}>
                  <circle cx="239" cy={y} r="19" fill="var(--art-ground)" />
                  <path
                    d={`M230 ${y}h18M239 ${y - 9}v18`}
                    opacity={i === 1 ? 0 : 0.7}
                  />
                  <circle
                    cx="239"
                    cy={y}
                    r={i === 1 ? 9 : 4}
                    fill="currentColor"
                  />
                  <path d={`M258 ${y}h36`} strokeDasharray="2 4" />
                </g>
              ))}
              <circle cx="83" cy="95" r="6" fill="currentColor" />
            </>
          ) : null}
          {motif === "feedback" ? (
            <>
              <path
                d="M75 70C75 18 245 18 245 70M245 121c0 52-170 52-170 0"
                strokeWidth="2"
              />
              <path d="m68 61 7 9 7-9m156 69 7-9 7 9" strokeWidth="2" />
              <rect x="41" y="70" width="68" height="51" rx="4" />
              <rect x="211" y="70" width="68" height="51" rx="4" />
              <path d="M109 95h102" strokeDasharray="3 5" />
              {[0, 1, 2, 3, 4].map((i) => (
                <path key={i} d={`M${225 + i * 10} 81v29`} opacity=".5" />
              ))}
              <rect
                x="60"
                y="85"
                width="30"
                height="21"
                fill="currentColor"
                opacity=".8"
              />
              <text x="160" y="45" textAnchor="middle" className={styles.text}>
                LATENT
              </text>
              <text x="160" y="153" textAnchor="middle" className={styles.text}>
                TOKEN
              </text>
            </>
          ) : null}
          {motif === "grammar" ? (
            <>
              {[0, 1, 2].map((i) => (
                <g
                  key={i}
                  transform={`translate(${52 + i * 36} ${31 + i * 22})`}
                >
                  <rect width="146" height="84" fill="var(--art-ground)" />
                  <path d="M0 24h146M36 24v60" opacity=".5" />
                  <path
                    d="M49 40h71M49 54h52M49 68h63"
                    strokeWidth="3"
                    opacity=".45"
                  />
                  <rect
                    x="10"
                    y="8"
                    width="29"
                    height="7"
                    fill="currentColor"
                    stroke="none"
                  />
                </g>
              ))}
            </>
          ) : null}
          {motif === "memory" ? (
            <>
              {[
                { x: 95, y: 69 },
                { x: 215, y: 69 },
                { x: 155, y: 131 },
              ].map(({ x, y }, g) => (
                <g key={x}>
                  <circle
                    cx={x}
                    cy={y}
                    r="41"
                    strokeDasharray="2 4"
                    opacity=".5"
                  />
                  {Array.from({ length: 9 }, (_, i) => (
                    <circle
                      key={i}
                      cx={x + Math.cos(i * 2.4) * ((i % 3) * 9 + 8)}
                      cy={y + Math.sin(i * 2.4) * ((i % 3) * 9 + 8)}
                      r={g === 1 ? 4 : 3}
                      fill="currentColor"
                      opacity={0.35 + (i % 3) * 0.25}
                    />
                  ))}
                </g>
              ))}
              <path d="m131 68 44 0m-61 38 17 9m52-9-17 9" opacity=".6" />
            </>
          ) : null}
          {motif === "receipt" ? (
            <>
              <path
                d="M101 24h118v143l-10-5-10 5-10-5-10 5-10-5-10 5-10-5-10 5-10-5-10 5-8-5V24Z"
                fill="var(--art-ground)"
              />
              <text x="118" y="70" className={styles.number}>
                118%
              </text>
              {[91, 111, 131].map((y) => (
                <g key={y}>
                  <circle cx="122" cy={y} r="5" />
                  <path d={`m119 ${y} 2 2 4-4M137 ${y}h60`} opacity=".65" />
                </g>
              ))}
              <path d="M63 62h22M235 126h22M257 116v20M63 52v20" opacity=".5" />
            </>
          ) : null}
          {motif === "gates" ? (
            <>
              <path d="M23 95h274" strokeDasharray="3 5" opacity=".5" />
              {[0, 1, 2, 3, 4].map((i) => (
                <g key={i}>
                  <rect
                    x={43 + i * 49}
                    y={59 - i * 6}
                    width="33"
                    height={72 + i * 12}
                    fill="var(--art-ground)"
                  />
                  <path d={`m${53 + i * 49} 95 5 5 9-12`} strokeWidth="2" />
                  <text
                    x={59 + i * 49}
                    y="165"
                    textAnchor="middle"
                    className={styles.text}
                  >
                    0{i + 1}
                  </text>
                </g>
              ))}
            </>
          ) : null}
          {motif === "interfaces" ? (
            <>
              <path
                d="M84 49h35l41 46 41-46h35M84 141h35l41-46 41 46h35"
                opacity=".65"
              />
              {(
                [
                  [51, 30],
                  [236, 30],
                  [51, 121],
                  [236, 121],
                ] as const
              ).map(([x, y], i) => (
                <g key={i}>
                  <rect
                    x={x}
                    y={y}
                    width="33"
                    height="38"
                    rx="4"
                    fill="var(--art-ground)"
                  />
                  <path
                    d={`M${x + 8} ${y + 12}h17M${x + 8} ${y + 20}h11M${x + 8} ${y + 28}h14`}
                    opacity=".6"
                  />
                </g>
              ))}
              <circle cx="160" cy="95" r="30" fill="var(--art-ground)" />
              <circle cx="160" cy="95" r="19" />
              <circle cx="160" cy="95" r="9" fill="currentColor" />
            </>
          ) : null}
          {motif === "context" ? (
            <>
              {(
                [
                  [47, 44],
                  [47, 116],
                  [126, 80],
                  [238, 80],
                ] as const
              ).map(([x, y], i) => (
                <g key={i}>
                  <rect
                    x={x}
                    y={y}
                    width={i === 2 ? 54 : 36}
                    height="36"
                    rx="3"
                    fill={i === 2 ? "currentColor" : "var(--art-ground)"}
                  />
                  <path
                    d={`M${x + 8} ${y + 12}h20M${x + 8} ${y + 23}h14`}
                    stroke={i === 2 ? "var(--art-ground)" : "currentColor"}
                  />
                </g>
              ))}
              <path d="M83 62h16l27 36M83 134h16l27-36M180 98h58m-7-6 7 6-7 6" />
              <circle
                cx="153"
                cy="98"
                r="59"
                strokeDasharray="2 4"
                opacity=".3"
              />
            </>
          ) : null}
          {motif === "boundary" ? (
            <>
              <rect
                x="55"
                y="25"
                width="210"
                height="140"
                strokeDasharray="3 5"
                opacity=".45"
              />
              <rect x="80" y="43" width="160" height="104" />
              <rect
                x="107"
                y="63"
                width="106"
                height="64"
                fill="var(--art-ground)"
              />
              <circle cx="160" cy="95" r="17" />
              <path d="m152 94 6 6 12-13" strokeWidth="2" />
              <path d="M0 95h80M240 95h80" />
              <circle cx="80" cy="95" r="5" fill="currentColor" />
              <circle cx="240" cy="95" r="5" fill="currentColor" />
            </>
          ) : null}
          {motif === "probe" ? (
            <>
              {[58, 76, 95, 114, 132].map((y) => (
                <g key={y}>
                  <circle cx="30" cy={y} r="3" fill="currentColor" opacity=".7" />
                  <path d={`M36 ${y}C74 ${y} 76 95 110 95`} strokeDasharray="2 4" opacity=".6" />
                </g>
              ))}
              <rect x="110" y="48" width="96" height="94" rx="3" fill="currentColor" />
              <path
                d="M126 70h64M126 84h48M126 98h58M126 112h36"
                stroke="var(--art-ground)"
                opacity=".4"
              />
              <rect x="170" y="120" width="20" height="8" fill="var(--art-ground)" stroke="none" opacity=".75" />
              <path d="M206 95h16" strokeDasharray="2 4" opacity=".6" />
              <path d="M226 142h70" opacity=".5" />
              {[34, 52, 22, 64, 40, 70, 28, 46].map((h, i) => (
                <rect
                  key={i}
                  x={228 + i * 8.5}
                  y={142 - h}
                  width="5"
                  height={h}
                  fill="currentColor"
                  stroke="none"
                  opacity={i === 5 ? 0.9 : 0.55}
                />
              ))}
              <text x="261" y="160" textAnchor="middle" className={styles.text}>
                SIGNATURE
              </text>
            </>
          ) : null}
        </g>
      </svg>
    </div>
  );
}
